import React, { useCallback, useEffect, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Input,
  Button,
  Icon,
  Spinner,
  Collapse,
  FormControl,
  FormLabel,
} from "@chakra-ui/react";
import {
  HiOutlineTrash,
  HiPlus,
  HiChevronDown,
  HiChevronUp,
  HiOutlineUserGroup,
  HiOutlineLink,
  HiOutlineEnvelope,
  HiOutlineClipboardDocument,
  HiOutlineArrowPath,
} from "react-icons/hi2";
import { BASE_API_URL } from "../Components/config";
import { PALETTES } from "./classroomColors";
import { useClassroomToast } from "./classroomToast";

const API = `${BASE_API_URL}/api/v1`;

const formatDate = (value) => {
  if (!value) return "No due date";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "No due date";
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const ManageClassroomModal = ({
  isOpen,
  onClose,
  courseGuid,
  classroomName,
  isDark = true,
  onCourseUpdated,
}) => {
  const p = isDark ? PALETTES.dark : PALETTES.light;
  const toast = useClassroomToast(isDark);
  const token = localStorage.getItem("accessToken");
  const authHeaders = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  const [name, setName] = useState(classroomName || "");
  const [savingName, setSavingName] = useState(false);

  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [openStudent, setOpenStudent] = useState(null);

  // All attempts for this course, grouped per student.
  const [attemptsByStudent, setAttemptsByStudent] = useState({});

  // An address with no account yet, waiting on "invite them?".
  const [inviteCandidate, setInviteCandidate] = useState(null);
  const [sendingInvite, setSendingInvite] = useState(null);
  const [invites, setInvites] = useState([]);
  const [cancelling, setCancelling] = useState(null);
  const [joinToken, setJoinToken] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  // Built from wherever the app is open, so it matches the site in use.
  const joinUrl = joinToken ? `${window.location.origin}/join/${joinToken}` : "";

  const loadStudents = useCallback(async () => {
    setLoadingStudents(true);
    try {
      const res = await fetch(`${API}/user-course-links/students-by-course-guid/${courseGuid}`, {
        headers: authHeaders,
      });
      setStudents(res.ok ? await res.json() : []);
    } catch {
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseGuid]);

  const loadAttempts = useCallback(async () => {
    try {
      const res = await fetch(
        `${API}/user-course-links/user-paper-attempts/detail/?course_user_type=INSTRUCTOR&course_guid=${courseGuid}&limit=200`,
        { headers: authHeaders }
      );
      if (!res.ok) return;
      const rows = await res.json();
      const grouped = {};
      (rows || []).forEach((row) => {
        if (!row.user_guid) return;
        if (!grouped[row.user_guid]) grouped[row.user_guid] = [];
        grouped[row.user_guid].push(row);
      });
      setAttemptsByStudent(grouped);
    } catch {
      setAttemptsByStudent({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseGuid]);

  const loadInvites = useCallback(async () => {
    try {
      const res = await fetch(`${API}/course-invites/course/${courseGuid}/email`, { headers: authHeaders });
      setInvites(res.ok ? await res.json() : []);
    } catch {
      setInvites([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseGuid]);

  const loadJoinLink = useCallback(async () => {
    try {
      const res = await fetch(`${API}/course-invites/course/${courseGuid}/link`, { headers: authHeaders });
      if (res.ok) setJoinToken((await res.json()).token);
    } catch {
      /* the card offers to try again */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseGuid]);

  useEffect(() => {
    if (!isOpen || !courseGuid) return;
    setName(classroomName || "");
    setInviteCandidate(null);
    loadStudents();
    loadAttempts();
    loadInvites();
    loadJoinLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, courseGuid]);

  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed || trimmed === classroomName) return;
    setSavingName(true);
    try {
      const res = await fetch(`${API}/courses/${courseGuid}`, {
        method: "PUT",
        headers: authHeaders,
        body: JSON.stringify({ name: trimmed }),
      });
      if (!res.ok) throw new Error();
      toast({ title: "Course name updated", status: "success" });
      if (typeof onCourseUpdated === "function") onCourseUpdated(trimmed);
    } catch {
      toast({ title: "Couldn't update the course name", status: "error" });
    } finally {
      setSavingName(false);
    }
  };

  const addStudent = async () => {
    const email = newEmail.trim();
    if (!email) return;
    setAdding(true);
    setInviteCandidate(null);
    try {
      const userRes = await fetch(`${API}/users/by-email/${encodeURIComponent(email)}`, { headers: authHeaders });
      if (userRes.status === 404) {
        // No account with that address: offer to invite them instead.
        setInviteCandidate(email);
        return;
      }
      if (!userRes.ok) throw new Error("User not found");
      const user = await userRes.json();

      const linkRes = await fetch(`${API}/user-course-links/`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({
          user_guid: user.guid,
          course_guid: courseGuid,
          course_user_type: "STUDENT",
        }),
      });
      if (!linkRes.ok) throw new Error("Failed to add student");

      toast({ title: "Student added", description: `${email} was added to the course.`, status: "success" });
      setNewEmail("");
      loadStudents();
    } catch (e) {
      toast({
        title: "Couldn't add student",
        description:
          e?.message === "User not found"
            ? "No account was found with that email. The student must sign up first."
            : "Something went wrong. Please try again.",
        status: "error",
      });
    } finally {
      setAdding(false);
    }
  };

  const sendInvite = async (email) => {
    setSendingInvite(email);
    try {
      const res = await fetch(`${API}/course-invites/course/${courseGuid}/email`, {
        method: "POST",
        headers: authHeaders,
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "");
      if (data.email_sent) {
        toast({ title: "Invite sent", description: `${email} will get an email with a link to join.`, status: "success" });
      } else {
        toast({
          title: "Invite saved, but the email didn't go out",
          description: "Try sending it again in a moment, or share the class's join link.",
          status: "warning",
        });
      }
      setInviteCandidate(null);
      setNewEmail("");
      loadInvites();
    } catch (e) {
      toast({ title: "Couldn't send the invite", description: e.message || "Please try again.", status: "error" });
    } finally {
      setSendingInvite(null);
    }
  };

  const cancelInvite = async (invite) => {
    if (!window.confirm(`Cancel the invite to ${invite.email}? Its link will stop working.`)) return;
    setCancelling(invite.guid);
    try {
      const res = await fetch(`${API}/course-invites/${invite.guid}`, { method: "DELETE", headers: authHeaders });
      if (!res.ok) throw new Error();
      setInvites((prev) => prev.filter((i) => i.guid !== invite.guid));
      toast({ title: "Invite cancelled", status: "success" });
    } catch {
      toast({ title: "Couldn't cancel the invite", status: "error" });
    } finally {
      setCancelling(null);
    }
  };

  const copyJoinLink = async () => {
    try {
      await navigator.clipboard.writeText(joinUrl);
      toast({ title: "Join link copied", description: "Anyone with it can join this class as a student.", status: "success" });
    } catch {
      toast({ title: "Couldn't copy the link", description: "Select it and copy it by hand.", status: "error" });
    }
  };

  const resetJoinLink = async () => {
    if (!window.confirm("Reset the join link? The current link will stop working straight away.")) return;
    setLinkBusy(true);
    try {
      const res = await fetch(`${API}/course-invites/course/${courseGuid}/link/reset`, {
        method: "POST",
        headers: authHeaders,
      });
      if (!res.ok) throw new Error();
      setJoinToken((await res.json()).token);
      toast({ title: "Join link reset", description: "The old link no longer works.", status: "success" });
    } catch {
      toast({ title: "Couldn't reset the link", status: "error" });
    } finally {
      setLinkBusy(false);
    }
  };

  const removeStudent = async (student) => {
    const label = [student.first_name, student.last_name].filter(Boolean).join(" ") || student.email;
    if (!window.confirm(`Remove ${label} from this course?`)) return;
    setRemoving(student.user_guid);
    try {
      const res = await fetch(
        `${API}/user-course-links/students/${courseGuid}/${student.user_guid}`,
        { method: "DELETE", headers: authHeaders }
      );
      if (!res.ok) throw new Error();
      setStudents((prev) => prev.filter((s) => s.user_guid !== student.user_guid));
      toast({ title: "Student removed", status: "success" });
    } catch {
      toast({ title: "Couldn't remove this student", status: "error" });
    } finally {
      setRemoving(null);
    }
  };

  const studentName = (s) =>
    [s.first_name, s.last_name].filter(Boolean).join(" ").trim() || s.email;

  const sectionTitle = {
    fontSize: "12px",
    fontWeight: "600",
    color: p.textMuted,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    mb: "10px",
  };

  const card = {
    background: p.surface,
    border: "1px solid",
    borderColor: p.border,
    borderRadius: "12px",
    padding: "18px",
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg={p.overlay} backdropFilter="blur(2px)" />
      <ModalContent
        className="stande-classroom"
        data-theme={isDark ? "dark" : "light"}
        bg={p.page}
        color={p.text}
        borderRadius="16px"
        border="1px solid"
        borderColor={p.border}
        boxShadow="0 24px 60px -20px rgba(0,0,0,0.5)"
        maxHeight="88vh"
        mx="16px"
      >
        <ModalHeader fontSize="18px" fontWeight="600" letterSpacing="-0.2px">
          Manage space
        </ModalHeader>
        <ModalCloseButton borderRadius="full" color={p.textSub} _hover={{ bg: p.hover }} />

        <ModalBody pb="24px">
          <VStack spacing="18px" align="stretch">
            {/* Course details */}
            <Box {...card}>
              <Text {...sectionTitle}>Course details</Text>
              <FormControl>
                <FormLabel fontSize="13px" fontWeight="500" color={p.textSub} mb="6px">
                  Course name
                </FormLabel>
                <HStack spacing="8px">
                  <Input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveName(); } }}
                    height="40px"
                    borderRadius="10px"
                    bg={p.inputBg}
                    color={p.text}
                    border="1px solid"
                    borderColor={p.inputBorder}
                    fontSize="14px"
                    _hover={{ borderColor: p.textMuted }}
                    _focus={{ borderColor: p.accent, boxShadow: `0 0 0 1px ${p.accent}` }}
                  />
                  <Button
                    variant="unstyled"
                    display="inline-flex"
                    alignItems="center"
                    px="18px"
                    height="40px"
                    borderRadius="full"
                    bg={p.accent}
                    color={p.onAccent}
                    fontSize="13px"
                    fontWeight="600"
                    _hover={{ bg: p.accentHover }}
                    onClick={saveName}
                    isLoading={savingName}
                    isDisabled={!name.trim() || name.trim() === classroomName}
                    opacity={!name.trim() || name.trim() === classroomName ? 0.6 : 1}
                  >
                    Save
                  </Button>
                </HStack>
              </FormControl>
            </Box>

            {/* Join link */}
            <Box {...card}>
              <HStack spacing="8px" mb="6px">
                <Icon as={HiOutlineLink} boxSize="16px" color={p.textSub} />
                <Text {...sectionTitle} mb="0">Join link</Text>
              </HStack>
              <Text fontSize="13px" color={p.textSub} mb="12px">
                Share this with your students. Anyone with the link can join as a student, and anyone
                without an account can sign up on the way. Reset it to stop the old link working.
              </Text>
              {joinToken ? (
                <HStack spacing="8px">
                  <Input
                    value={joinUrl}
                    isReadOnly
                    onFocus={(e) => e.target.select()}
                    height="38px"
                    borderRadius="10px"
                    bg={p.surfaceSubtle}
                    color={p.text}
                    border="1px solid"
                    borderColor={p.inputBorder}
                    fontSize="13px"
                  />
                  <Button
                    leftIcon={<Icon as={HiOutlineClipboardDocument} boxSize="15px" />}
                    height="38px"
                    px="14px"
                    borderRadius="10px"
                    bg={p.accent}
                    color={p.onAccent}
                    fontSize="13px"
                    fontWeight="600"
                    flexShrink={0}
                    _hover={{ bg: p.accentHover }}
                    onClick={copyJoinLink}
                  >
                    Copy
                  </Button>
                  <Button
                    leftIcon={<Icon as={HiOutlineArrowPath} boxSize="15px" />}
                    variant="outline"
                    height="38px"
                    px="12px"
                    borderRadius="10px"
                    borderColor={p.border}
                    color={p.text}
                    fontSize="13px"
                    fontWeight="500"
                    flexShrink={0}
                    _hover={{ bg: p.hover }}
                    onClick={resetJoinLink}
                    isLoading={linkBusy}
                  >
                    Reset
                  </Button>
                </HStack>
              ) : (
                <Button size="sm" variant="outline" borderColor={p.border} color={p.text} onClick={loadJoinLink}>
                  Get the join link
                </Button>
              )}
            </Box>

            {/* Students */}
            <Box {...card}>
              <HStack justifyContent="space-between" alignItems="center" mb="10px">
                <HStack spacing="8px">
                  <Icon as={HiOutlineUserGroup} boxSize="16px" color={p.textSub} />
                  <Text {...sectionTitle} mb="0">Students</Text>
                </HStack>
                <Box background={p.surfaceSubtle} border="1px solid" borderColor={p.border} color={p.textSub} fontSize="11px" fontWeight="600" px="10px" py="3px" borderRadius="full">
                  {students.length}
                </Box>
              </HStack>

              {/* Add a student */}
              <HStack spacing="8px" mb="14px">
                <Input
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addStudent(); } }}
                  placeholder="student@email.com"
                  height="38px"
                  borderRadius="10px"
                  bg={p.inputBg}
                  color={p.text}
                  border="1px solid"
                  borderColor={p.inputBorder}
                  fontSize="13.5px"
                  _placeholder={{ color: p.textMuted }}
                  _hover={{ borderColor: p.textMuted }}
                  _focus={{ borderColor: p.accent, boxShadow: `0 0 0 1px ${p.accent}` }}
                />
                <Button
                  leftIcon={<Icon as={HiPlus} boxSize="15px" />}
                  variant="outline"
                  height="38px"
                  borderRadius="10px"
                  borderColor={p.border}
                  color={p.text}
                  fontSize="13px"
                  fontWeight="500"
                  flexShrink={0}
                  _hover={{ bg: p.hover }}
                  onClick={addStudent}
                  isLoading={adding}
                  isDisabled={!newEmail.trim()}
                >
                  Add
                </Button>
              </HStack>

              {/* No account with that address: invite them instead. */}
              {inviteCandidate && (
                <Box
                  mb="14px"
                  padding="12px 14px"
                  borderRadius="10px"
                  border="1px solid"
                  borderColor={p.border}
                  background={p.infoSoft}
                >
                  <Text fontSize="13.5px" fontWeight="600" color={p.text}>
                    {inviteCandidate} doesn't have a Stande account yet
                  </Text>
                  <Text fontSize="12.5px" color={p.textSub} mt="2px">
                    Invite them? They'll get an email with a link to sign up, and they'll join this class
                    as soon as they log in.
                  </Text>
                  <HStack spacing="8px" mt="10px">
                    <Button
                      leftIcon={<Icon as={HiOutlineEnvelope} boxSize="15px" />}
                      height="32px"
                      px="14px"
                      borderRadius="full"
                      bg={p.accent}
                      color={p.onAccent}
                      fontSize="12.5px"
                      fontWeight="600"
                      _hover={{ bg: p.accentHover }}
                      onClick={() => sendInvite(inviteCandidate)}
                      isLoading={sendingInvite === inviteCandidate}
                    >
                      Send invite
                    </Button>
                    <Button
                      height="32px"
                      px="12px"
                      variant="ghost"
                      borderRadius="full"
                      color={p.textSub}
                      fontSize="12.5px"
                      _hover={{ bg: p.hover }}
                      onClick={() => setInviteCandidate(null)}
                    >
                      Cancel
                    </Button>
                  </HStack>
                </Box>
              )}

              {/* Invited, not joined yet. */}
              {invites.length > 0 && (
                <VStack spacing="6px" align="stretch" mb="14px">
                  {invites.map((inv) => (
                    <HStack
                      key={inv.guid}
                      justifyContent="space-between"
                      padding="8px 12px"
                      border="1px dashed"
                      borderColor={p.border}
                      borderRadius="10px"
                    >
                      <HStack spacing="10px" minWidth="0">
                        <Icon as={HiOutlineEnvelope} boxSize="15px" color={p.textMuted} flexShrink={0} />
                        <Box minWidth="0">
                          <Text fontSize="13px" fontWeight="500" color={p.text} noOfLines={1}>
                            {inv.email}
                          </Text>
                          <Text fontSize="11.5px" color={p.textMuted}>
                            {inv.last_sent_at ? `Invited ${formatDate(inv.last_sent_at)}` : "Email not sent yet"}
                          </Text>
                        </Box>
                      </HStack>
                      <HStack spacing="2px" flexShrink={0}>
                        <Button
                          size="xs"
                          variant="ghost"
                          color={p.textSub}
                          fontSize="12px"
                          _hover={{ bg: p.hover }}
                          onClick={() => sendInvite(inv.email)}
                          isLoading={sendingInvite === inv.email}
                        >
                          Resend
                        </Button>
                        <Button
                          size="xs"
                          variant="ghost"
                          color="#E53E3E"
                          fontSize="12px"
                          _hover={{ bg: p.hover }}
                          onClick={() => cancelInvite(inv)}
                          isLoading={cancelling === inv.guid}
                        >
                          Cancel
                        </Button>
                      </HStack>
                    </HStack>
                  ))}
                </VStack>
              )}

              {loadingStudents ? (
                <Flex justify="center" py="20px">
                  <Spinner size="sm" color={p.accent} />
                </Flex>
              ) : students.length === 0 ? (
                <Text fontSize="13px" color={p.textMuted} textAlign="center" py="16px">
                  No students in this course yet. Add or invite one by email above, or share the join link.
                </Text>
              ) : (
                <VStack spacing="6px" align="stretch">
                  {students.map((s) => {
                    const attempts = attemptsByStudent[s.user_guid] || [];
                    const isOpenRow = openStudent === s.user_guid;
                    return (
                      <Box
                        key={s.user_guid}
                        border="1px solid"
                        borderColor={p.border}
                        borderRadius="10px"
                        background={p.surface}
                        overflow="hidden"
                      >
                        <HStack justifyContent="space-between" padding="10px 12px">
                          <HStack
                            spacing="10px"
                            flex="1"
                            minWidth="0"
                            cursor="pointer"
                            onClick={() => setOpenStudent(isOpenRow ? null : s.user_guid)}
                          >
                            <Icon
                              as={isOpenRow ? HiChevronUp : HiChevronDown}
                              boxSize="15px"
                              color={p.textMuted}
                              flexShrink={0}
                            />
                            <Box minWidth="0">
                              <Text fontSize="13.5px" fontWeight="600" color={p.text} noOfLines={1}>
                                {studentName(s)}
                              </Text>
                              <Text fontSize="11.5px" color={p.textMuted} noOfLines={1}>
                                {s.email}
                              </Text>
                            </Box>
                            <Box
                              background={p.surfaceSubtle}
                              border="1px solid"
                              borderColor={p.border}
                              color={p.textSub}
                              fontSize="10.5px"
                              fontWeight="600"
                              px="8px"
                              py="2px"
                              borderRadius="full"
                              flexShrink={0}
                            >
                              {attempts.length} assigned
                            </Box>
                          </HStack>
                          <Button
                            size="xs"
                            variant="ghost"
                            color="#E53E3E"
                            leftIcon={<Icon as={HiOutlineTrash} boxSize="14px" />}
                            fontSize="12px"
                            _hover={{ bg: p.hover }}
                            onClick={() => removeStudent(s)}
                            isLoading={removing === s.user_guid}
                            flexShrink={0}
                          >
                            Remove
                          </Button>
                        </HStack>

                        <Collapse in={isOpenRow} animateOpacity>
                          <Box borderTop="1px solid" borderColor={p.border} padding="10px 12px" background={p.surfaceSubtle}>
                            {attempts.length === 0 ? (
                              <Text fontSize="12px" color={p.textMuted}>
                                No assignments or quizzes assigned yet.
                              </Text>
                            ) : (
                              <VStack spacing="6px" align="stretch">
                                {attempts.map((a) => (
                                  <HStack
                                    key={a.guid}
                                    justifyContent="space-between"
                                    padding="8px 10px"
                                    borderRadius="8px"
                                    background={p.surface}
                                    border="1px solid"
                                    borderColor={p.border}
                                  >
                                    <Box minWidth="0">
                                      <Text fontSize="12.5px" fontWeight="600" color={p.text} noOfLines={1}>
                                        {a.paper_name || "Untitled paper"}
                                      </Text>
                                      <Text fontSize="11px" color={p.textMuted}>
                                        {(a.paper_class || a.paper_type || "Paper").toString().replace(/_/g, " ")}
                                        {" · "}Due {formatDate(a.due_date)}
                                      </Text>
                                    </Box>
                                    <Text
                                      fontSize="10.5px"
                                      fontWeight="600"
                                      flexShrink={0}
                                      color={a.completion_status === "COMPLETE" ? "#38A169" : p.textSub}
                                    >
                                      {a.completion_status === "COMPLETE" ? "Completed" : "Not completed"}
                                    </Text>
                                  </HStack>
                                ))}
                              </VStack>
                            )}
                          </Box>
                        </Collapse>
                      </Box>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ManageClassroomModal;
